import React, { FC } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  AssistMigrationService,
  AssistMigrationServiceCategory,
} from 'types/assist/assistMigration';
import { FaqEditor } from './FaqEditor';

const PRICING_TYPE_OPTIONS = [
  { value: 'FIXED_PRICE', label: 'per unit' },
  { value: 'PER_POUND', label: 'per pound' },
];

interface ServiceCategoriesEditorProps {
  value: AssistMigrationServiceCategory[];
  onChange: (next: AssistMigrationServiceCategory[]) => void;
}

export const ServiceCategoriesEditor: FC<ServiceCategoriesEditorProps> = ({ value, onChange }) => {
  const setCategory = (ci: number, patch: Partial<AssistMigrationServiceCategory>) =>
    onChange(value.map((cat, i) => (i === ci ? { ...cat, ...patch } : cat)));
  const setService = (ci: number, si: number, patch: Partial<AssistMigrationService>) =>
    setCategory(ci, {
      services: (value[ci].services ?? []).map((svc, i) => (i === si ? { ...svc, ...patch } : svc)),
    });

  return (
    <div>
      {value.map((category, ci) => (
        <Box
          key={ci}
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mb: 1 }}
        >
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <TextField
              size="small"
              label="Category"
              value={category.name}
              fullWidth
              onChange={(event) => setCategory(ci, { name: event.target.value })}
            />
            <Button
              size="small"
              color="error"
              onClick={() => onChange(value.filter((_, i) => i !== ci))}
            >
              ×
            </Button>
          </div>
          <TextField
            size="small"
            label="Category guidance for agent (optional)"
            value={category.message ?? ''}
            fullWidth
            multiline
            helperText="Caller-facing facts shared by every service in this category."
            style={{ marginBottom: 6 }}
            onChange={(event) => setCategory(ci, { message: event.target.value })}
          />
          {(category.services ?? []).map((service, si) => (
            <Box key={si} sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1, mb: 1.25 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <TextField
                  size="small"
                  label="Service"
                  value={service.name}
                  fullWidth
                  onChange={(event) => setService(ci, si, { name: event.target.value })}
                />
                <TextField
                  size="small"
                  label="Price"
                  type="number"
                  style={{ width: 128 }}
                  value={service.price}
                  onChange={(event) =>
                    setService(ci, si, {
                      price: event.target.value === '' ? -1 : Number(event.target.value),
                    })
                  }
                />
                <TextField
                  size="small"
                  label="Type"
                  select
                  style={{ width: 148 }}
                  value={service.pricingType}
                  onChange={(event) =>
                    setService(ci, si, {
                      pricingType: event.target.value as AssistMigrationService['pricingType'],
                    })
                  }
                >
                  {PRICING_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  size="small"
                  color="error"
                  onClick={() =>
                    setCategory(ci, {
                      services: (category.services ?? []).filter((_, i) => i !== si),
                    })
                  }
                >
                  ×
                </Button>
              </div>
              <TextField
                size="small"
                label="Service guidance for agent (optional)"
                value={service.message ?? ''}
                fullWidth
                multiline
                helperText="Caller-facing facts that apply only to this service."
                style={{ marginBottom: 6 }}
                onChange={(event) => setService(ci, si, { message: event.target.value })}
              />
              <Typography variant="caption" style={{ fontWeight: 600, display: 'block' }}>
                Service FAQs
              </Typography>
              <FaqEditor
                items={service.faqs ?? []}
                onChange={(faqs) => setService(ci, si, { faqs })}
              />
            </Box>
          ))}
          <Button
            size="small"
            onClick={() =>
              setCategory(ci, {
                services: [
                  ...(category.services ?? []),
                  { name: '', price: -1, pricingType: 'FIXED_PRICE', message: '' },
                ],
              })
            }
          >
            + service
          </Button>
        </Box>
      ))}
      <Button
        size="small"
        onClick={() => onChange([...value, { name: '', message: '', services: [] }])}
      >
        + category
      </Button>
    </div>
  );
};
